import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { DiscountsApi } from "@/openapi/generated";
import type { UpdateTuitionDiscountRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import type { useToast } from "@/components/feedback";

export const useDiscountUpdate = (
  toast: ReturnType<typeof useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (updateDiscountRequest: UpdateTuitionDiscountRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(DiscountsApi, accessToken);

      return service.apiDiscountsDiscountIdPut({
        discountId: updateDiscountRequest.discountId,
        updateTuitionDiscountRequest: updateDiscountRequest,
      });
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Discounts],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Discount, variables.discountId],
      });
      toast({
        title: "Discount updated",
        description: "Discount updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update discount";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};